import {coordsToKey} from "../coords/coordsToKey";
import {HEX_DIRECTIONS} from "../../constants/geometry";
import {TERRAIN_TOKENS} from "../../constants/tokens";

/**
 * Доступные ячейки перемещения ЛЕТАЮЩЕГО отряда (см. спеку
 * flying-unit-movement). Перемещение — одно действие до конечной ячейки, как у
 * всех; отличие только в том, что промежуточные ячейки пути ничего не
 * ограничивают: летун проходит над горами, чужими и своими отрядами, городами
 * и пустыми ячейками (правило «путь не продолжается дальше пустой ячейки» к
 * полету не применяется). Встать можно на ячейку без отряда, не гору и не из
 * noStopHexKeys.
 *
 * Море — не часть поля (старое обозначение рамки): над ним не летают, и оно не
 * входит в расстояние. Поэтому расстояние — по ячейкам поля (BFS), а не
 * геометрическое: так и путь для анимации никогда не проходит через море.
 *
 * Форма ответа та же, что у calculateAvailableMovementCells: paths — кратчайший
 * путь по полю до каждой посещенной ячейки (по нему анимируется ход, в том
 * числе чужой — см. resolveUnitMovementPath).
 *
 * @param {Object} hexGridRenderer — из него берется только наличие ячеек
 * @param {Object} hexes — жетоны ячеек
 * @param {Array} units
 * @param {Object} selectedUnit
 * @param {number} baseSpeed — дальность отряда без доп. снабжения
 * @param {number} speed — дальность с учетом доп. снабжения
 * @param {Set<string>} noStopHexKeysSet
 */
export const calculateFlyingMovementCells = (
  hexGridRenderer, hexes, units, selectedUnit, baseSpeed, speed, noStopHexKeysSet
) => {
  const occupiedHexKeys = new Set(units.map(unit => unit.hexKey));
  const reachableCells = [];
  const additionalSupplyCells = [];
  const [startQ, startR] = selectedUnit.hexKey.split(',').map(Number);
  const paths = {[selectedUnit.hexKey]: [{q: startQ, r: startR}]};

  let frontier = [[startQ, startR]];
  for (let depth = 1; depth <= speed && frontier.length > 0; depth++) {
    const nextFrontier = [];

    for (const [q, r] of frontier) {
      const parentKey = coordsToKey(q, r);

      for (const [dq, dr] of HEX_DIRECTIONS) {
        const newQ = q + dq;
        const newR = r + dr;
        const newKey = coordsToKey(newQ, newR);

        if (paths[newKey] || !hexGridRenderer.hexes[newKey]) {
          continue;
        }

        const hexToken = hexes[newKey];
        if (hexToken === TERRAIN_TOKENS.SEA) {
          continue;
        }

        paths[newKey] = [...paths[parentKey], {q: newQ, r: newR}];
        nextFrontier.push([newQ, newR]);

        const canStopHere = hexToken !== TERRAIN_TOKENS.MOUNTAIN
          && !occupiedHexKeys.has(newKey)
          && !noStopHexKeysSet.has(newKey);

        if (canStopHere) {
          reachableCells.push(newKey);
          if (depth > baseSpeed) {
            additionalSupplyCells.push(newKey);
          }
        }
      }
    }

    frontier = nextFrontier;
  }

  return {
    cells: reachableCells,
    additionalSupplyCells,
    paths,
  };
};
